import { useEffect, useState } from "react";

export type Theme =
  | "platinum"
  | "amber"
  | "velvet"
  | "bordeaux"
  | "dusk"
  | "mist"
  | "arctic";

const THEME_KEY = "pulse.theme";

const THEMES: Theme[] = [
  "platinum",
  "amber",
  "velvet",
  "bordeaux",
  "dusk",
  "mist",
  "arctic",
];

/** Formula-driven theme brightness scale (0–100). */
const THEME_BRIGHTNESS: Record<Theme, number> = {
  platinum: 10,
  amber: 10,
  velvet: 10,
  bordeaux: 10,
  dusk: 32,
  mist: 52,
  arctic: 92,
};

/** Legacy localStorage values from removed hand-crafted themes. */
const LEGACY_THEME_ALIASES: Record<string, Theme> = {
  dark: "platinum",
  warm: "amber",
};

function normalizeTheme(value: string | null): Theme | null {
  if (!value) return null;
  if (value in LEGACY_THEME_ALIASES) {
    const legacy = LEGACY_THEME_ALIASES[value];
    if (legacy) return legacy;
  }
  if (THEMES.includes(value as Theme)) return value as Theme;
  return null;
}

export function getThemeBrightnessLevel(theme: Theme): number {
  return THEME_BRIGHTNESS[theme];
}

/** Light map tiles / UI treatment when brightness is in the upper band (Mist, Arctic). */
export function isLightConsumerTheme(theme: Theme): boolean {
  return getThemeBrightnessLevel(theme) >= 50;
}

export function getStoredTheme(): Theme {
  if (typeof window === "undefined") return "platinum";
  return normalizeTheme(localStorage.getItem(THEME_KEY)) ?? "platinum";
}

export function applyTheme(theme: Theme) {
  document.documentElement.setAttribute("data-theme", theme);
  localStorage.setItem(THEME_KEY, theme);
}

export function initTheme() {
  const raw = localStorage.getItem(THEME_KEY);
  const theme = normalizeTheme(raw) ?? "platinum";
  if (raw && raw !== theme) {
    localStorage.setItem(THEME_KEY, theme);
  }
  applyTheme(theme);
}

/** Live consumer theme from `data-theme` (settings, init, cross-tab). */
export function useConsumerTheme(): Theme {
  const [theme, setTheme] = useState<Theme>(() => getStoredTheme());

  useEffect(() => {
    const root = document.documentElement;

    const sync = () => {
      const attr = root.getAttribute("data-theme");
      const next = normalizeTheme(attr) ?? getStoredTheme();
      setTheme(next);
    };

    sync();

    const observer = new MutationObserver(sync);
    observer.observe(root, { attributes: true, attributeFilter: ["data-theme"] });

    const onStorage = (event: StorageEvent) => {
      if (event.key === THEME_KEY) sync();
    };
    window.addEventListener("storage", onStorage);

    return () => {
      observer.disconnect();
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  return theme;
}
